import { type HistoricalPrice, type CollectibleId } from "@/services/collectibles";
import type { CategoryId } from "@/services/collection/categories";
import { type ApiResponseImage } from "@/services/images";
import type { z } from "zod";
import type { PriceNumeric } from "@/services/prices";
import { type Price, type PriceNumericOrEmpty } from "@/services/prices";
import { type MarketplaceItemId } from "@/services/marketplace/item";
import type { CollectionItemId_Schema } from "@/services/collection/item/collectionItemSchema";
import type { Uuid } from "@/types";
import type { CollectibleElastic } from "@/services/collectibles/elastic";

export type CollectionItemId = z.infer<typeof CollectionItemId_Schema>;

export type CollectionItem = {
  id: CollectionItemId;

  acquirePrice: PriceNumericOrEmpty;
  currentPrice: PriceNumericOrEmpty;
  lastPrice: PriceNumericOrEmpty;
  publicOfferedPrice: Price;

  categoryId: CategoryId;
  dateAcquired: string; // YYYY-MM-DD
  images: ApiResponseImage[];
  name: string;
  parameters: CollectionItemParameter[];

  note: string | null;
  publicNote: string;

  pieces: number;
  publicPieces: number;

  userCollectionList: string[];
  whereStored: string;
  acquisitionSource: string;
  statusList: ItemStatus[];

  likesCount: number;
  likedByCurrentUser: boolean;

  marketPlaceItem: MarketplaceItemBasicInfo | null;

  // @todo Would be nice to get rid off this property - needs BE rework
  detail?: CollectionItemDetail;

  collectible?: CollectionItemDetail;
  collectibleElastic: CollectibleElastic | null; // @todo no transform() called!!!
  aiIdentificationId: Uuid | null;
};

export type MarketplaceItemBasicInfo = {
  id: MarketplaceItemId;
  name: string;
  quantity: number;
};

export type CollectionItemWithMarketplaceItem = CollectionItem & {
  marketPlaceItem: MarketplaceItemBasicInfo;
};

export type CollectionItemWithDetail = CollectionItem & {
  detail: CollectionItemDetail;
};

export type CollectionItemWithCollectible = CollectionItem & {
  collectible: CollectionItemDetail_Collectible;
};
export type CollectionItemWithDetail_LegoSet = CollectionItem & {
  categoryId: CategoryId.LegoSets;
  detail: CollectionItemDetail_LegoSet;
};

export type CollectionItemDetail = CollectionItemDetail_Collectible | CollectionItemDetail_LegoSet;

/** In long term we want to drop this specific detail. */
export type CollectionItemDetail_LegoSet = CollectionItemDetail_Collectible & {
  theme: { name: string };
  number: string;
  partsCount: string;
  year: string;
  dimensions: string;
  weight: string;
};

export type CollectionItemDetail_Collectible = {
  id: CollectibleId;
  name: string;
  prices: HistoricalPrice[];
  images: ApiResponseImage[];
  parameters: {
    option: {
      value: string;
    };
    parameter: {
      id: number;
      label: string;
      isWidgetIdentificator?: true;
    };
  }[];
};

// @todo revision needed, what is really used?? Should the type be even here?
export type CollectionItemParameter = {
  isCustom: 0 | 1;
  parameterId: number;
  value: string;
  valueId: number;
};

export type CreateCollectionItemData = {
  categoryId: CategoryId;
  userCollectionListName?: string;
} & (
  | {
      collectibleId: CollectibleId;
      // temp properties, will be removed after full lego migration into UU
      set: CollectibleId;
      minifigure: CollectibleId;
    }
  | {
      customName: string;
    }
);

export type ApiData_UpdateCollectionItem = Partial<{
  acquirePrice: PriceNumericOrEmpty;
  dateAcquired: string;
  currentPrice: Price;
  customName: string;
  note: string;
  publicNote: string;
  pieces: number;
  where_stored: string;
  acquisition_source: string;
  aiIdentificationSourceId: string;
}>;

export enum ItemStatus {
  InMyCollection = "inCollection",
  NotInCollection = "notInCollection",
  OnMarketplace = "onMarketplace",
  Archived = "archived",
}

export type CollectionItem_ListItem_MarketplaceItemBasicInfo = {
  id: MarketplaceItemId;
  price: PriceNumeric;
  name?: string;
  quantity: number;
};

export type CollectionItem_ListItem = {
  id: CollectionItemId;
  name: string;
  categoryId: CategoryId;
  acquirePrice: PriceNumericOrEmpty;
  publicOfferedPrice: Price;
  currentPrice: PriceNumericOrEmpty;
  images: ApiResponseImage[];
  isOwnershipTransferRequested: boolean;
  likesCount: number;
  statusList: ItemStatus[];
  userCollectionList: string[];
  publicPieces: number;
  publicNote: string;
  aiIdentificationId: Uuid | null;

  marketPlaceItem: CollectionItem_ListItem_MarketplaceItemBasicInfo | null;
};

export type CollectionItem_ListItemWithMarketplaceItem = CollectionItem_ListItem & {
  marketPlaceItem: CollectionItem_ListItem_MarketplaceItemBasicInfo;
};

export enum Api_ItemPatchOperation {
  Archivate = "archivate",
}
